import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Button, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Spin } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { createCatalog, getCatalogs, updateCatalog } from "../services/catalogueApi";
import FormField from "../../../Components/Common/Form/FormField";

const AddEditCatalogForm = ({ modal, setModal, toggle, selectedCatalog }) => {
  const queryClient = useQueryClient();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const { isLoading: isLoadingCategory, isError: isErrorCategory, error: errorCategory, data: catalogs } = useQuery("catalogs", getCatalogs);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Oups!!! Veuillez renseigner le nom"),
    category: Yup.string().required('Veuillez renseigner la catégorie de ce statut'),
    description: Yup.string().required("Une description serait idéale pour se retrouvée.")
  });

  const initialValues = selectedCatalog ? {
    name: selectedCatalog.name,
    category: selectedCatalog.category,
    type: selectedCatalog.type,
    question: selectedCatalog.question,
    description: selectedCatalog.description
  } : {
    name: "",
    category: "",
    customCategory: "",
    type: "",
    question: "",
    description: ""
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsLoadingSubmit(true);
      try {
        if (values.customCategory) {
          values.category = values.customCategory;
        }
        
        if (selectedCatalog) {
          await updateCatalog(selectedCatalog?.id, { ...values });
          toast.success("Le catalogue a été modifié avec succès 🎉");
        } else {
          await createCatalog({ ...values });
          toast.success("Le catalogue a été créé avec succès 🎉");
        }

        await queryClient.invalidateQueries("catalogs");
        toggle();
        formik.resetForm();
      } catch (error) {
        if (selectedCatalog) {
          console.error("Erreur modification catalogue :", error?.message || error);
          toast.error("😌 Erreur lors de la modification du catalogue :", error?.message || error);
        } else {
          console.error("Erreur lors de l'ajout du catalogue :", error?.message || error);
          toast.error("😌 Erreur lors de l'ajout du catalogue :", error?.message || error);
        }
      } finally {
        setIsLoadingSubmit(false);
      }
    },
  });

  useEffect(() => {
    formik.setValues(initialValues);
  }, [selectedCatalog]);

  const handleCategoryChange = (selectedOption) => {
    if (selectedOption.value === 'autre') {
      formik.setFieldValue('category', 'autre');
      formik.setFieldValue('customCategory', '');
    } else {
      formik.setFieldValue('category', selectedOption.value);
      formik.setFieldValue('customCategory', '');
    }
  };

  const categoryOptions = (catalogs && Array.isArray(catalogs)) 
    ? catalogs
        .map((category) => category.category)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((category) => ({
          value: category,
          label: category.charAt(0).toUpperCase() + category.slice(1)
        }))
    : [];

  const categoryOptionsWithOther = [
    ...categoryOptions,
    { value: 'autre', label: 'Autre' }
  ];

  const modalTitle = selectedCatalog ? "Édition" : "Ajout";

  return (
    <Modal
      id="showModalClient"
      isOpen={modal}
      toggle={toggle}
      centered
      backdrop={"static"}
      style={{ maxWidth: "700px" }}
    >
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        <CheckCircleOutlined /> {modalTitle} d'un catalogue (statut, instruction, type d'inspections, ...)
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <FormField label="Nom" name="name" formik={formik} colSize={12} placeholder="Entrez le nom du statut ou autres"/>
            <div className="mb-3">
              <label htmlFor="category" className="form-label">Catégorie</label>
              <Select
                id="category"
                name="category"
                options={categoryOptionsWithOther}
                onChange={handleCategoryChange}
                value={categoryOptionsWithOther.find(option => option.value === formik.values.category) || null}
                placeholder="Commences à écrire pour choisir un catégory"
                isClearable
                isSearchable
              />
            </div>

            {formik.values.category === 'autre' && (
              <FormField label="Autre Catégorie à saisir" name="customCategory" formik={formik} placeholder="Entrez une catégorie personnalisée"/>
            )}
            
            {(formik.values.category === 'inspectionCible' || formik.values.category === 'inspectionComplete' || formik.values.category === 'Incident') && (
              <>
                <FormField label="Type" name="type" formik={formik} colSize={12} placeholder="Entrez le type de l'inspection"/>
                <FormField label="Question" name="question" formik={formik} colSize={12} placeholder="Entrez la question de l'inspection"/>
              </>
            )}

            <FormField label="Intruction" name="description" formik={formik} colSize={12} placeholder="Entrez la description du catalogue" isTextarea={true}/>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            Annuler
          </Button>
          <Button type="submit" className="btn btn-soft-success" disabled={isLoadingSubmit}>
            {isLoadingSubmit ? <Spin size="small" text="En cours" /> : (selectedCatalog ? "Modifier" : "Ajouter")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddEditCatalogForm;
